/**
 * Action wrapper for generating a document.
 *
 * Exports both a controlled dialog (`GenerateDocumentDialog`) for use inside
 * dropdown menus and a self-contained action (`GenerateDocumentAction`) with
 * a render prop trigger.
 */
import { useState } from 'react'

import type { PatientWithOwners } from '@snootsvet/shared'

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { GenerateDocumentScreen } from '@/frontend/features/printing/documents/generate-document-screen'

interface GenerateDocumentDialogProps {
  patient: PatientWithOwners
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Controlled dialog for the document generation screen. */
export function GenerateDocumentDialog({
  patient,
  open,
  onOpenChange,
}: GenerateDocumentDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Generate Document — {patient.name}</DialogTitle>
        </DialogHeader>
        <GenerateDocumentScreen patient={patient} />
      </DialogContent>
    </Dialog>
  )
}

interface GenerateDocumentActionProps {
  patient: PatientWithOwners
  render: (props: { onClick: () => void }) => React.ReactNode
}

/** Self-contained action with render prop trigger and built-in dialog. */
export function GenerateDocumentAction({ patient, render }: GenerateDocumentActionProps) {
  const [open, setOpen] = useState(false)

  return (
    <>
      {render({ onClick: () => setOpen(true) })}
      <GenerateDocumentDialog patient={patient} open={open} onOpenChange={setOpen} />
    </>
  )
}
